import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFocusEffect } from 'expo-router';
import { useAtomValue } from 'jotai';
import { AlertTriangle, Info } from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { ExpertMobileNumberSetupModal } from '@/components/ExpertMobileNumberSetupModal';
import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { Alert, AlertIcon, AlertText } from '@/components/ui/alert';
import { Box } from '@/components/ui/box';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { ScrollView } from '@/components/ui/scroll-view';
import { Switch } from '@/components/ui/switch';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import {
  EXPERT_ONLY_NOTIFICATION_SECTION_TYPES,
  NOTIFICATION_CHANNEL_COLUMNS,
  NOTIFICATION_SETTINGS_COPY,
  NOTIFICATION_SETTINGS_SECTIONS,
  type NotificationChannelRow,
  type NotificationSettingsSection,
} from '@/constants/notificationSettings';
import { ROUTES } from '@/constants/routes';
import { useColors } from '@/hooks/useColors';
import { useEnablePush } from '@/hooks/useEnablePush';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import { CurrentUserV2, getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import {
  NotificationPreferenceBodyV2,
  setNotificationPreferenceV2,
} from '@/services/setNotificationPreferenceV2';
import {
  getPushNotificationPermission,
  subscribeToPushPermissionChange,
} from '@/util/pushNotifications';
import { queryClient } from '@/util/queryClient';

const SCREEN_COLUMN = getScreenColumnClasses({ maxWidth: 'max-w-screen-md' });

const capitalize = (str: string) => {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
};

/**
 * Fixed width for each channel toggle column so the column headers stay
 * aligned with the switches beneath them across all rows. Wider on desktop
 * where there is room to breathe.
 */
const CHANNEL_COLUMN_CLASS = 'w-[68px] items-center md:w-24';

export default function NotificationSettings() {
  const userId = useAtomValue(userIdAtom);

  const userQueryOptions = getCurrentUserV2QueryOptions(userId);

  const userQueryKey = userQueryOptions.queryKey;

  const { data, refetch } = useQuery(userQueryOptions);

  const [pushNotificationGranted, setPushNotificationGranted] = useState(false);

  const [showMobileSetupModal, setShowMobileSetupModal] = useState(false);

  /**
   * Re-read OS push permission (and refresh prefs) every time the screen
   * regains focus, not just on mount, so a grant made outside this screen —
   * another prompt, or the browser's own site settings — reflects here on
   * navigate-back rather than showing a stale Push column.
   */
  useFocusEffect(
    useCallback(() => {
      refetch();

      (async () => setPushNotificationGranted(await getPushNotificationPermission()))();
    }, [refetch])
  );

  /**
   * A grant can happen without this screen losing focus — from the browser's
   * own site settings, or from another surface rendered as a sibling rather
   * than on top. Focus alone would miss those, so subscribe to permission
   * changes and let the Push column flip live, no reload. No-op on native
   * (see pushNotifications platform split).
   */
  useEffect(() => subscribeToPushPermissionChange(setPushNotificationGranted), []);

  const enablePush = useEnablePush('notification_settings');

  /**
   * Whether the "Turn on" button can do anything. On web the page can only
   * raise the OS prompt while permission is `default`/`prompt`; once `denied`
   * it can't re-prompt, so we fall back to settings-guidance text instead. On
   * native the single request handles both (it routes to device settings when
   * denied), so the button is always offered there.
   */
  const canPromptForPush =
    !isWeb || (typeof Notification !== 'undefined' && Notification.permission !== 'denied');

  /**
   * Tracks the in-flight opt-in so the button can show it is working. Push
   * initialization is waited on before the permission dialog can be raised,
   * and when the provider's SDK is blocked that wait runs to its timeout, so
   * without this the control sits inert for seconds and reads as broken.
   */
  const [isEnablingPush, setIsEnablingPush] = useState(false);

  const handleEnablePush = async () => {
    setIsEnablingPush(true);

    try {
      setPushNotificationGranted(await enablePush());
    } finally {
      setIsEnablingPush(false);
    }
  };

  const notificationsPreferencesSections = useMemo(() => {
    const sections = NOTIFICATION_SETTINGS_SECTIONS.map((section) => ({
      ...section,
      data: section.data.map((item) => {
        if (section.type === 'communityNotifications') {
          const communityNotificationKeys = [
            'likeComment',
            'newCommentOnPostYouCommentedOn',
            'replyToComment',
            'likeCommunityPost',
            'commentOnYourCommunityPost',
          ];

          const isAnyEnabled = communityNotificationKeys.some(
            (key) =>
              data?.notificationsPreferences?.[
                `${key}${item.method}` as keyof typeof data.notificationsPreferences
              ]
          );

          return {
            ...item,
            value: isAnyEnabled,
          };
        }

        return {
          ...item,
          value:
            data?.notificationsPreferences?.[
              `${section.type}${item.method}` as keyof typeof data.notificationsPreferences
            ] || false,
        };
      }),
    }));

    if (data?.userType === 'expert') {
      return sections;
    }

    return sections.filter(
      (section) => !EXPERT_ONLY_NOTIFICATION_SECTION_TYPES.includes(section.type)
    );
  }, [data]);

  const { mutate: mutateV2 } = useMutation({
    mutationFn: setNotificationPreferenceV2,
    onMutate: async ({
      channel,
      event,
      enabled,
      username,
    }: NotificationPreferenceBodyV2 & { username?: string }) => {
      await queryClient.cancelQueries({ queryKey: userQueryKey });

      const previousUserData = await queryClient.getQueryData(userQueryKey);

      queryClient.setQueryData(userQueryKey, (old: CurrentUserV2 | undefined) => {
        if (!old) {
          return old;
        }

        if (event === 'all') {
          const updatedPreferences = { ...old.notificationsPreferences } as NonNullable<
            CurrentUserV2['notificationsPreferences']
          >;

          Object.keys(updatedPreferences).forEach((key) => {
            if (key.endsWith(capitalize(channel))) {
              updatedPreferences[key as keyof typeof updatedPreferences] = enabled;
            }
          });

          return {
            ...old,
            notificationsPreferences: updatedPreferences,
          };
        }

        if (event === 'communityNotifications') {
          const updatedPreferences = { ...old.notificationsPreferences } as NonNullable<
            CurrentUserV2['notificationsPreferences']
          >;

          const communityNotificationKeys = [
            'likeComment',
            'newCommentOnPostYouCommentedOn',
            'replyToComment',
            'likeCommunityPost',
            'commentOnYourCommunityPost',
          ];

          communityNotificationKeys.forEach((key) => {
            const preferenceKey = `${key}${capitalize(channel)}` as keyof typeof updatedPreferences;

            if (preferenceKey in updatedPreferences) {
              updatedPreferences[preferenceKey] = enabled;
            }
          });

          return {
            ...old,
            notificationsPreferences: updatedPreferences,
          };
        }

        return {
          ...old,
          notificationsPreferences: {
            ...old.notificationsPreferences,
            [`${event}${capitalize(channel)}`]: enabled,
          } as NonNullable<CurrentUserV2['notificationsPreferences']>,
        };
      });

      return { previousUserData };
    },
    onError: (error, newNotificationsPreference, context) => {
      console.error(
        `NotificationSettings setNotificationPreference:${JSON.stringify(newNotificationsPreference)} ERROR:`,
        error
      );

      queryClient.setQueryData(userQueryKey, context?.previousUserData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKey });
    },
  });

  const { colors } = useColors();

  const phoneOnFile = Boolean(data?.mobilePhone);

  const { bottom } = useSafeAreaInsets();

  // Matrix (aligned channel columns) needs horizontal room, so it's used
  // only at md+. On compact widths the channels stack as labeled rows.
  const isCompact = useIsCompactLayout();

  /**
   * Renders a single channel toggle with its disabled state, accessibility
   * metadata, and optimistic mutation. Shared by both the desktop matrix
   * column and the compact stacked row so the toggle behavior can't drift.
   */
  const renderChannelSwitch = (
    section: NotificationSettingsSection,
    item: NotificationChannelRow
  ) => {
    const isDisabled =
      (!phoneOnFile && item.method === 'Sms') ||
      (!pushNotificationGranted && item.method === 'Push');

    let disabledHint: string | undefined;

    if (isDisabled && item.method === 'Sms') {
      disabledHint = NOTIFICATION_SETTINGS_COPY.smsDisabledHint;
    } else if (isDisabled && item.method === 'Push') {
      disabledHint = NOTIFICATION_SETTINGS_COPY.pushDisabledHint;
    }

    return (
      <Switch
        accessibilityLabel={`${item.title} for ${section.title}`}
        accessibilityState={{ checked: item.value, disabled: isDisabled }}
        accessibilityHint={disabledHint}
        trackColor={{ true: colors.secondary[500], false: colors.background[200] }}
        value={item.value}
        isDisabled={isDisabled}
        onValueChange={(value: boolean) => {
          const eventType =
            section.type === 'communityNotifications'
              ? 'communityNotifications'
              : (section.type as NotificationPreferenceBodyV2['event']);

          const username =
            data?.firstName && data?.lastName ? `${data.firstName} ${data.lastName}` : undefined;

          mutateV2({
            event: eventType,
            channel: item.method.toLowerCase() as NotificationPreferenceBodyV2['channel'],
            enabled: value,
            username,
          });
        }}
      />
    );
  };

  return (
    <VStack className="flex-1" style={{ paddingBottom: bottom }}>
      <Header
        contentScrollsUnderHeader
        title={NOTIFICATION_SETTINGS_COPY.screenTitle}
        statusBarBackgroundColor={colors.background[0]}
        backRoute={ROUTES.ACCOUNT}
        isBackButton
      />

      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className={twJoin('flex-1', SCREEN_COLUMN.frame)}
        showsVerticalScrollIndicator={false}
        contentContainerClassName={twJoin('py-6', SCREEN_COLUMN.content)}>
        <Text>{NOTIFICATION_SETTINGS_COPY.intro}</Text>

        {/* Channel-setup notices, same inline Alert pattern for each blocked
            channel: no phone (Text) and push permission off (Push). */}
        {!phoneOnFile && (
          <Alert action="warning" className="mt-3 items-center gap-2 rounded-md">
            <AlertIcon as={AlertTriangle} className="shrink-0" />
            <AlertText size="sm" className="flex-1">
              {NOTIFICATION_SETTINGS_COPY.noMobileNumber}
            </AlertText>
            <Button
              action="primary"
              variant="solid"
              size="sm"
              className="shrink-0"
              onPress={() => setShowMobileSetupModal(true)}
              accessibilityLabel={NOTIFICATION_SETTINGS_COPY.addNumberA11yLabel}>
              <ButtonText>{NOTIFICATION_SETTINGS_COPY.addNumberLabel}</ButtonText>
            </Button>
          </Alert>
        )}

        {/* Status for the disabled Push column (an Alert, not body text, so it
            reads distinctly from the instructional intro). When the OS prompt
            is still reachable it offers a one-tap "Turn on" — the only in-app
            path to (re-)enable push, since the soft-ask modal shows once per
            device and the toggles can't request permission. When push is
            blocked at the OS level it points to settings instead, the only
            place that can re-enable it. Mirrors the SMS "Add phone" Alert. */}
        {!pushNotificationGranted && (
          <Alert
            action={canPromptForPush ? 'info' : 'warning'}
            className="mt-3 items-center gap-2 rounded-md">
            <AlertIcon as={canPromptForPush ? Info : AlertTriangle} className="shrink-0" />
            <AlertText size="sm" className="flex-1">
              {canPromptForPush
                ? NOTIFICATION_SETTINGS_COPY.pushOffPromptable
                : NOTIFICATION_SETTINGS_COPY.pushOffBlocked}
            </AlertText>

            {canPromptForPush ? (
              <Button
                action="primary"
                variant="solid"
                size="sm"
                className="shrink-0"
                isDisabled={isEnablingPush}
                onPress={handleEnablePush}
                accessibilityLabel={NOTIFICATION_SETTINGS_COPY.turnOnPushA11yLabel}>
                {isEnablingPush ? <ButtonSpinner /> : null}

                <ButtonText>{NOTIFICATION_SETTINGS_COPY.turnOnPushLabel}</ButtonText>
              </Button>
            ) : null}
          </Alert>
        )}

        {/* Channel column headers — md+ matrix only (compact stacks instead). */}
        {!isCompact ? (
          <>
            <HStack className="items-end gap-2 pb-2 pt-6">
              <Box className="flex-1" />

              {NOTIFICATION_CHANNEL_COLUMNS.map((column) => (
                <Box key={column.method} className={CHANNEL_COLUMN_CLASS}>
                  <Text size="sm" className="font-semibold text-typography-700">
                    {column.columnLabel}
                  </Text>
                </Box>
              ))}
            </HStack>

            <Divider />
          </>
        ) : null}

        {notificationsPreferencesSections.map((section) => (
          <Box key={section.type}>
            {isCompact ? (
              <VStack className="gap-3 py-4">
                <VStack className="gap-0.5">
                  <Text className="font-semibold">{section.title}</Text>

                  {section.description && (
                    <Text size="sm" className="text-typography-700">
                      {section.description}
                    </Text>
                  )}
                </VStack>

                <VStack className="gap-3">
                  {section.data.map((item) => (
                    <HStack key={item.method} className="items-center justify-between">
                      <Text>{item.title}</Text>

                      {renderChannelSwitch(section, item)}
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            ) : (
              <HStack className="items-center gap-2 py-4">
                <VStack className="flex-1 gap-0.5 pr-2">
                  <Text className="font-semibold">{section.title}</Text>

                  {section.description && (
                    <Text size="sm" className="text-typography-700">
                      {section.description}
                    </Text>
                  )}
                </VStack>

                {section.data.map((item) => (
                  <Box key={item.method} className={CHANNEL_COLUMN_CLASS}>
                    {renderChannelSwitch(section, item)}
                  </Box>
                ))}
              </HStack>
            )}

            <Divider />
          </Box>
        ))}
      </ScrollView>

      <ExpertMobileNumberSetupModal
        isOpen={showMobileSetupModal}
        source="notification_settings"
        onClose={() => setShowMobileSetupModal(false)}
        onPhoneNumberSet={() => {
          refetch();

          setShowMobileSetupModal(false);
        }}
      />
    </VStack>
  );
}
